import { ctaButton } from '@/modules/parts/ctaButton';
import { isReelLike } from '@/helpers/isReelLike';
import { preventFullscreen } from '@/modules/actions/preventFullscreen';
import { applyMediaLongPressHandlers } from '@/modules/actions/mobile/applyMediaLongPressHandlers';
import { setAdBadge } from '@/helpers/adBadge';
import { applyVideoDownloadSafeguards } from '@/helpers/preventVideoDownload';
import { updateCtaHostPositionInWrapper } from '@/helpers/ctaHostPosition';
import { applyVideoThumbnailGuard } from '@/helpers/mediaUtils';
import { deferMediaSrc } from '@/helpers/deferredMediaSrc';
import { applyFeedFolderAspectFrame } from '@/helpers/feedFolderAspectFrame';
import { setFeedFolderSaleBadge } from '@/helpers/feedFolderSaleBadge';
import { getStoreeelModalRoot } from '@/helpers/storeeelShadowDom';

const DISABLE_PRESERVE_3D_CSS = `
        .storeeel-modal .swiper-3d .swiper-slide,
        .storeeel-modal .swiper-3d .swiper-cube-shadow {
          transform-style: initial !important;
        }
      `;
const DISABLE_PRESERVE_3D_STYLE_ID = 'disable-preserve-3d';

// iPhone 用 3D 無効化スタイルを、モーダルが Shadow DOM 化されていれば Shadow Root 内へ、
// 互換のため Light DOM の document.head にも注入する
const injectDisablePreserve3DStyle = () => {
  const modalRoot = getStoreeelModalRoot();
  if (modalRoot && !modalRoot.getElementById?.(DISABLE_PRESERVE_3D_STYLE_ID)) {
    const shadowStyle = document.createElement('style');
    shadowStyle.id = DISABLE_PRESERVE_3D_STYLE_ID;
    shadowStyle.textContent = DISABLE_PRESERVE_3D_CSS;
    modalRoot.appendChild(shadowStyle);
  }
  if (!document.getElementById(DISABLE_PRESERVE_3D_STYLE_ID)) {
    const headStyle = document.createElement('style');
    headStyle.id = DISABLE_PRESERVE_3D_STYLE_ID;
    headStyle.textContent = DISABLE_PRESERVE_3D_CSS;
    document.head.appendChild(headStyle);
  }
};

// options.deferSrc: SP で開いた位置以外のスライドはメディアの src を保留し、読み込み順制御に委ねる
// （全スライドを同時に読み込んで、今見ているコンテンツの帯域を奪わないようにする）
export const createSlide = (content, { deferSrc = false } = {}) => {
  const shareState = window.__storeeelShareOpen;
  const shareCampaignMatch = shareState
    && (!shareState.params?.campaignId || String(shareState.params.campaignId) === String(window.storeeelItems?.id));
  const shareTargetContentId = shareState?.targetContentId ?? null;
  const isShareTarget = shareCampaignMatch
    && shareTargetContentId != null
    && String(content?.id) === String(shareTargetContentId);

  // iPhone のときのみ、3D キューブの preserve-3d を無効化
  if (window.isIphone) {
    injectDisablePreserve3DStyle();
  }
  const slide = document.createElement('div');
  slide.className = 'storeeel-swiper-group-wrapper swiper-slide';
  if (window.storeeelItems.isMobile) {
    slide.style.height = '100dvh';
    slide.style.background = '#000';
    slide.style.inset = '0';
    slide.style.margin = '0';
    slide.style.padding = '0';
  }

  // モバイルかつcircleでない場合のみ、slide の高さを 100dvh に固定
  if (window.storeeelItems.isMobile && window.storeeelItems.frame !== 'circle') {
    slide.style.height = '100dvh';
  }

  const asset = content.mediaAsset;
  const isImage = asset.attachmentData.metadata.mime_type.includes('image');

  const mediaEl = isImage ? document.createElement('img') : document.createElement('video');
  mediaEl.className = isImage ? 'storeeel-media-image' : 'storeeel-movie';
  mediaEl.classList.add('active');
  mediaEl.setAttribute('media-name', asset.name);
  mediaEl.setAttribute('media-id', asset.id);
  mediaEl.setAttribute('content-id', content.id);
  mediaEl.setAttribute('group-id', content.contentGroupId);
  // フィード商品の content-id / group-id は抽選のたびに変わる仮想IDのため、共有リンクでは再現できない。
  // 共有URL生成（sharingClickAction）が使う安定IDを、フィード由来のときだけ添える。
  if (content?.isFeedFolderContent) {
    mediaEl.setAttribute('feed-source-content-id', content.feedSourceContentId ?? '');
    mediaEl.setAttribute('feed-product-id', content.feedProductId ?? '');
  }
  if (shareCampaignMatch) {
    if (isImage) {
      mediaEl.loading = isShareTarget ? 'eager' : 'lazy';
      mediaEl.decoding = 'async';
      mediaEl.setAttribute('fetchpriority', isShareTarget ? 'high' : 'low');
    } else {
      mediaEl.preload = isShareTarget ? 'auto' : 'metadata';
      mediaEl.setAttribute('preload', isShareTarget ? 'auto' : 'metadata');
    }
  }
  const shouldDeferSrc = deferSrc && !isShareTarget;
  if (shouldDeferSrc) {
    deferMediaSrc(mediaEl, asset.mediaUrl);
  } else {
    mediaEl.src = asset.mediaUrl;
  }
  if (window.storeeelItems.isMobile) {
    // モバイルの場合、maxの高さを 100dvh に設定
    mediaEl.style.maxHeight = '100dvh';
  }

  if (!isImage) {
    applyVideoDownloadSafeguards(mediaEl);
    // SP時にうまく動作しない処理のために追加
    // モバイルでフルスクリーンを防ぎ、インライン再生を有効化
    // iPhone のときのみ全画面抑制を実施
    if (window.isIphone) {
      preventFullscreen(mediaEl);
    }
    // 既存の属性設定
    mediaEl.setAttribute('playsinline', '');
    mediaEl.setAttribute('webkit-playsinline', '');
    // PCでは「中央スライドのみ再生」を徹底するため、autoplayに依存しない
    mediaEl.autoplay = window.storeeelItems.isMobile;
    if (window.storeeelItems.isMobile) {
      mediaEl.setAttribute('autoplay', '');
    } else {
      mediaEl.removeAttribute('autoplay');
    }
    mediaEl.setAttribute('muted', '');
    mediaEl.muted = true;
    mediaEl.style.transform = 'translateZ(0)';
    mediaEl.style.backfaceVisibility = 'hidden';
    mediaEl.pause();
    if (shouldDeferSrc) {
      // 上で付けた autoplay を外し、順番が来るまで一切取得させない
      deferMediaSrc(mediaEl, asset.mediaUrl);
    }
  }

  // 💡 PC/SP 分岐
  if (window.storeeelItems.isMobile) {
    // SP＋iPhone のときは画像を縦幅いっぱいにカバー
    mediaEl.style.height = '100dvh';
    mediaEl.style.objectFit = 'contain';
    mediaEl.style.width = '100%';
    // メディア要素を下揃えにする
    mediaEl.style.verticalAlign = 'bottom';
    mediaEl.style.position = 'absolute';
    mediaEl.style.top = '50%';
    mediaEl.style.left = '50%';
    // iOS Safari で Swiper の transform 中に video が真っ黒になることがあるため、
    // 3D transform を使って別レイヤー化し描画を安定させる。
    mediaEl.style.transform = 'translate3d(-50%, -50%, 0)';
    mediaEl.style.webkitTransform = 'translate3d(-50%, -50%, 0)';
    mediaEl.style.backfaceVisibility = 'hidden';
    mediaEl.style.webkitBackfaceVisibility = 'hidden';
  } else {
    mediaEl.style.width = '100%';
    mediaEl.style.height = 'auto';
    mediaEl.style.bottom = isImage ? '2%' : '4%';
    // PC時のメディア要素に角丸を適用
    mediaEl.style.borderRadius = '30px';
    // メディア要素を下揃えにする
    mediaEl.style.verticalAlign = 'bottom';
  }

  // 2025-05-03: 拡大縮小用のラッパー要素を追加
  const wrapper = document.createElement('div');
  wrapper.className = 'slide-media-wrapper';
  wrapper.style.background = '#000';
  wrapper.style.position = 'relative';
  wrapper.dataset.assetType = asset?.type || '';
  if (window.storeeelItems.isMobile) {
    // モバイルのストーリー型ではメディア切替直後に高さが潰れないように高さを確保
    wrapper.style.height = '100dvh';
  }
  // PC のときのみ角丸を適用
  if (!window.storeeelItems.isMobile) {
    wrapper.style.borderRadius = '30px';
  }
  wrapper.style.setProperty('-webkit-tap-highlight-color', 'transparent');
  wrapper.appendChild(mediaEl);
  // フィードフォルダ商品はGMCの商品画像（任意比率）をそのまま使うため、枠を9:16に強制する
  if (content?.isFeedFolderContent) {
    applyFeedFolderAspectFrame(wrapper, mediaEl);
  }
  // セール中の商品にSALEラベルを出す（管理画面で表示を選んだフィードフォルダのみ）
  setFeedFolderSaleBadge(wrapper, content);
  applyMediaLongPressHandlers(mediaEl);

  setAdBadge(wrapper, asset?.type === 'adMediaAsset');

  if (!isImage) {
    // 共有リンクで直接開く対象だけは、表示準備が整うまでサムネイルで待機させる。
    applyVideoThumbnailGuard(mediaEl, wrapper, content, asset, { force: isShareTarget });
  }

  // wrapperにグループIDを設定
  wrapper.setAttribute('group-id', content.contentGroupId);
  // 頻度挿入コンテンツかどうかを DOM に記録（イベント追跡で参照する）
  if (content.isFrequencyInsertContent) {
    wrapper.dataset.isFrequencyInsertContent = 'true';
    // FrequencyInsertContent の ID を記録し、DOM 経由の計測パスで media_id として使用する
    wrapper.dataset.frequencyInsertContentId = content.frequencyInsertContentId || '';
  }

  // SP＋circle、SP/PC＋square・tile フレーム時にCTAボタンを付与
  if (
    isReelLike(window.storeeelItems.frame) ||
      (window.storeeelItems.isMobile && window.storeeelItems.frame === 'circle')
  ) {
    const ctaHost = ctaButton(content);
    if (ctaHost) {
      // CTAがあるスライドは明示的にポインタ操作を許可
      wrapper.style.cursor = 'pointer';
      wrapper.appendChild(ctaHost);
      updateCtaHostPositionInWrapper(wrapper, { mediaEl });
    }
  }

  slide.appendChild(wrapper);

  return slide;
};
